import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import '../styles/home.css';

const FEATURES = [
  { icon: '🎵', title: 'Backing Tracks', desc: 'High-quality instrumental tracks across every genre. Preview 20 seconds before you commit.' },
  { icon: '📝', title: 'Synced Lyrics', desc: 'Real-time lyrics delivered via WebSockets so text always matches the music — zero lag.' },
  { icon: '🎙️', title: 'Record & Blend', desc: 'Record your vocals right in the browser. Fine-tune timing with sync controls before saving.' },
  { icon: '🤝', title: 'Collaborate', desc: 'Add tracks to a shared compilation. Multiple singers, one song. Up to the creator\'s set limit.' },
  { icon: '⚡', title: 'Live Rooms', desc: 'Socket.io-powered live rooms keep all participants seeing the exact same lyric simultaneously.' },
  { icon: '🛡️', title: 'Secure & Safe', desc: 'Email verification, captcha, session management, and rate limiting keep the stage clean.' },
];

const STEPS = [
  { num: '01', title: 'Create Your Profile', desc: 'Pick a username, choose your emoji, verify your email and you\'re ready.' },
  { num: '02', title: 'Pick a Song', desc: 'Browse the library, preview 20 seconds of any track, then click Sing.' },
  { num: '03', title: 'Sing & Record', desc: 'Lyrics scroll in real time. Hit record, sing along, adjust timing with the sync slider.' },
  { num: '04', title: 'Save & Share', desc: 'Save your take to a compilation. Invite others to add their voices.' },
];

export default function HomePage() {
  const { user } = useAuth();

  return (
    <div className="home-page">
      {/* ── Hero ───────────────────────────────────────────── */}
      <section className="hero">
        <div className="hero-glow hero-glow-left" />
        <div className="hero-glow hero-glow-right" />

        <div className="hero-content">
          <div className="hero-badge animate-fade-in">
            <span className="badge badge-purple">🎤 Now Live</span>
            Real-time lyric sync powered by Socket.io
          </div>

          <h1 className="hero-title animate-fade-in-up delay-100">
            Sing. Record.
            <br />
            <span className="gradient-text">Collaborate.</span>
          </h1>

          <p className="hero-subtitle animate-fade-in-up delay-200">
            Your own karaoke stage — backing tracks, synchronized lyrics,
            browser recording, and multi-singer compilations.
            No downloads needed.
          </p>

          <div className="hero-actions animate-fade-in-up delay-300">
            {user ? (
              <Link to="/songs" className="btn btn-primary btn-lg">
                🎵 Browse Songs
              </Link>
            ) : (
              <>
                <Link to="/register" className="btn btn-primary btn-lg">
                  🎤 Start Singing Free
                </Link>
                <Link to="/login" className="btn btn-secondary btn-lg">
                  Sign In
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Floating music notes */}
        <div className="hero-notes" aria-hidden="true">
          {['♪', '♫', '♩', '♬', '♪', '♫'].map((n, i) => (
            <span
              key={i}
              className="hero-note"
              style={{
                '--x': `${10 + i * 15}%`,
                '--delay': `${i * 0.8}s`,
                '--size': `${20 + (i % 3) * 12}px`,
              }}
            >
              {n}
            </span>
          ))}
        </div>
      </section>

      {/* ── Features ────────────────────────────────────────── */}
      <section className="features-section">
        <div className="container">
          <div className="section-header">
            <h2>Everything you need to perform</h2>
            <p className="text-secondary">Built with React, Node.js, Socket.io, and Howler.js</p>
          </div>

          <div className="features-grid">
            {FEATURES.map((f, i) => (
              <div key={i} className={`feature-card animate-fade-in-up delay-${(i % 4) * 100}`}>
                <div className="feature-icon">{f.icon}</div>
                <h3 className="feature-title">{f.title}</h3>
                <p className="feature-desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ────────────────────────────────────── */}
      <section className="steps-section">
        <div className="container">
          <div className="section-header">
            <h2>On stage in 4 steps</h2>
          </div>

          <div className="steps-grid">
            {STEPS.map((step, i) => (
              <div key={i} className="step-card">
                <div className="step-num">{step.num}</div>
                <h3 className="step-title">{step.title}</h3>
                <p className="step-desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Tech Stack ──────────────────────────────────────── */}
      <section className="tech-section">
        <div className="container">
          <div className="tech-card card">
            <h3 className="gradient-text" style={{ fontSize: 22, marginBottom: 20 }}>
              Built with modern tech
            </h3>
            <div className="tech-pills">
              {[
                ['⚛️', 'React 18'],
                ['🟢', 'Node.js'],
                ['🔌', 'Socket.io'],
                ['🔊', 'Howler.js'],
                ['🗄️', 'MySQL'],
                ['🔑', 'JWT Auth'],
                ['📧', 'Nodemailer'],
                ['🛡️', 'hCaptcha'],
                ['⚡', 'Express'],
                ['🔄', 'Sequelize ORM'],
              ].map(([icon, label]) => (
                <div key={label} className="tech-pill">
                  <span>{icon}</span>
                  <span>{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────────────── */}
      {!user && (
        <section className="cta-section">
          <div className="container">
            <div className="cta-card">
              <div className="cta-glow" />
              <h2>Ready to take the stage?</h2>
              <p>Join free. No credit card. Just your voice.</p>
              <Link to="/register" className="btn btn-primary btn-lg">
                🎤 Create Free Account
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ── Footer ──────────────────────────────────────────── */}
      <footer className="home-footer">
        <div className="container">
          <div className="footer-inner">
            <span className="gradient-text" style={{ fontWeight: 900 }}>🎤 KaraokeApp</span>
            <span className="text-muted text-sm">Built with React + Node.js + Socket.io</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
